// ==========================================
// src/pages/PagamentoMercadoPago.jsx
// TELA DE PAGAMENTO - QR Code PIX Mercado Pago
// ✅ CORRIGIDO: Adiciona motorista_id no INSERT
// ✅ CORRIGIDO: UPDATE com motorista_id para bypass RLS
// ✅ CORRIGIDO: Campos snake_case alinhados com Edge Function
// ==========================================

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  ArrowLeft, Loader2, CheckCircle, XCircle, Copy,
  RefreshCw, QrCode, Clock, Shield, Zap, FileText,
  AlertTriangle, Smartphone, CreditCard
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';

const TAXA_AGENCIAMENTO = 50.00;

export default function PagamentoMercadoPago() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [carga, setCarga] = useState(null);
  const [consultaId, setConsultaId] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [qrCodeBase64, setQrCodeBase64] = useState('');
  const [qrCodeText, setQrCodeText] = useState('');
  const [ticketUrl, setTicketUrl] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState(1800);

  // ✅ Lê dados de location.state OU sessionStorage
  useEffect(() => {
    console.log('📥 [PagamentoMercadoPago] location.state:', location.state);
    
    let cargaData = null;
    let consultaIdData = null;

    if (location.state?.carga) {
      cargaData = location.state.carga;
      consultaIdData = location.state.consultaId;
      console.log('✅ Dados de location.state:', cargaData);
    }

    if (!cargaData) {
      const saved = sessionStorage.getItem('pagamento_carga');
      console.log('📦 sessionStorage raw:', saved);
      
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          console.log('📦 sessionStorage parsed:', parsed);
          
          if (parsed.carga) {
            cargaData = parsed.carga;
            consultaIdData = parsed.consultaId || parsed.consulta_id;
            console.log('✅ Dados de sessionStorage:', cargaData);
          }
        } catch (e) {
          console.error('❌ Erro ao parse sessionStorage:', e);
        }
      }
    }

    if (cargaData) {
      setCarga(cargaData);
      setConsultaId(consultaIdData);
      console.log('✅ Carga setada:', cargaData.id);
    } else {
      console.error('❌ Nenhuma carga encontrada em location.state nem sessionStorage');
    }
  }, [location.state]);

  useEffect(() => {
    if (carga) {
      sessionStorage.setItem('pagamento_carga', JSON.stringify({ 
        carga, 
        consultaId 
      }));
    }
  }, [carga, consultaId]);

  const criarPagamento = useCallback(async () => {
    if (!carga || !user) {
      toast.error('Dados incompletos para pagamento');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      // ✅ CORRIGIDO: snake_case para alinhar com Edge Function
      const { data, error } = await supabase.functions.invoke('criar-pagamento-mercadopago', {
        body: {
          carga_id: carga.id,
          consulta_id: consultaId,
          motorista_id: user.id,
          email_motorista: user.email,
          nome_motorista: user.nome || user.email,
          valor: TAXA_AGENCIAMENTO,
          descricao: `Taxa agenciamento - ${carga.titulo || 'Carga BoraFrete'}`
        }
      });

      if (error || !data) {
        throw new Error(error?.message || 'Erro ao criar pagamento');
      }

      // ✅ CORRIGIDO: snake_case na resposta da Edge Function
      setPaymentId(data.payment_id);
      setQrCodeBase64(data.qr_code_base64);
      setQrCodeText(data.qr_code);
      setTicketUrl(data.ticket_url);
      setStatus('pending');
      setTimeLeft(1800);

      // ✅ CORRIGIDO: INSERT com motorista_id para RLS funcionar
      const { error: insertError } = await supabase.from('pagamentos').insert({
        payment_id: data.payment_id,
        valor: TAXA_AGENCIAMENTO,
        status: 'pendente',
        payment_method: 'pix',
        qr_code: data.qr_code,
        qr_code_base64: data.qr_code_base64,
        ticket_url: data.ticket_url,
        motorista_id: user?.id,  // ✅ ADICIONADO: motorista_id para RLS
        created_at: new Date().toISOString()
      });

      if (insertError) {
        console.warn('⚠️ Erro ao salvar pagamento no Supabase:', insertError);
        // Não quebra o fluxo, continua mesmo se der erro no Supabase
      }

      toast.success('QR Code gerado! Escaneie para pagar');

    } catch (error) {
      console.error('Erro:', error);
      setStatus('error');
      setErrorMsg(error.message || 'Erro ao gerar pagamento');
      toast.error('Erro: ' + error.message);
    }
  }, [carga, user, consultaId]);

  // Polling para verificar pagamento
  useEffect(() => {
    if (status !== 'pending' || !paymentId) return;

    const interval = setInterval(async () => {
      try {
        const { data, error } = await supabase.functions.invoke('consultar-pagamento-mercadopago', {
          body: { paymentId }
        });

        if (error) throw error;

        if (data?.isApproved) {
          setStatus('approved');
          clearInterval(interval);
          toast.success('Pagamento confirmado!');

          // ✅ CORRIGIDO: UPDATE com motorista_id para bypass RLS
          const { error: updateError } = await supabase.from('pagamentos').update({
            status: 'pago',
            data_pagamento: new Date().toISOString()
          })
          .eq('payment_id', paymentId)
          .eq('motorista_id', user?.id);  // ✅ ADICIONADO: filtro por motorista_id

          if (updateError) {
            console.warn('⚠️ Erro ao atualizar pagamento:', updateError);
          }

          if (consultaId) {
            await supabase.from('consultas').update({
              status: 'aprovado',
              data_resposta: new Date().toISOString()
            }).eq('id', consultaId);
          }

          if (carga?.id) {
            await supabase.from('cargas').update({
              status: 'aceita',
              motorista_id: user?.id,
              motorista_nome: user?.nome || user?.email,
              motorista_email: user?.email,
              data_aceite: new Date().toISOString()
            }).eq('id', carga.id);
          }

          // ✅ NOVO: Salvar carga no localStorage antes de ir pro contrato
          const cargaParaSalvar = {
            ...carga,
            status: 'aceita',
            etapa: 'contrato_assinado',
            contrato_liberado: true,
            contrato_liberado_em: new Date().toISOString(),
            pagamento_confirmado: true,
            data_pagamento: new Date().toISOString(),
            motorista_id: user?.id,
            motorista_nome: user?.nome || user?.email,
            motorista_email: user?.email
          };
          
          const cargas = JSON.parse(localStorage.getItem('borafrete_cargas') || '[]');
          const index = cargas.findIndex(c => c.id === carga.id);
          if (index >= 0) {
            cargas[index] = { ...cargas[index], ...cargaParaSalvar };
          } else {
            cargas.push(cargaParaSalvar);
          }
          localStorage.setItem('borafrete_cargas', JSON.stringify(cargas));
          console.log('✅ Carga salva no localStorage:', cargaParaSalvar);

          const contratos = JSON.parse(localStorage.getItem('borafrete_contratos') || '[]');
          contratos.push({
            id: `contrato_${Date.now()}`,
            carga_id: carga.id,
            motorista_id: user?.id,
            motorista_nome: user?.nome || user?.email,
            motorista_email: user?.email,
            empresa_id: carga.empresa_id,
            status: 'aceito',
            dataAceite: new Date().toISOString(),
            valor: carga.valor_frete || carga.valor,
            taxa_agenciamento: TAXA_AGENCIAMENTO
          });
          localStorage.setItem('borafrete_contratos', JSON.stringify(contratos));

          setTimeout(() => {
            navigate(`/contrato/${carga.id}`, {
              state: { carga, fromPagamento: true, paymentConfirmed: true }
            });
          }, 3000);

        } else if (data?.status === 'rejected' || data?.status === 'cancelled') {
          setStatus('rejected');
          clearInterval(interval);
          toast.error('Pagamento rejeitado');
        }
      } catch (error) {
        console.error('Erro no polling:', error);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [status, paymentId, carga, consultaId, user, navigate]);

  useEffect(() => {
    if (status !== 'pending' || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setStatus('error');
          setErrorMsg('QR Code expirado. Gere um novo.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [status, timeLeft]);

  const copiarCodigo = async () => {
    if (!qrCodeText) return;
    try {
      await navigator.clipboard.writeText(qrCodeText);
      setCopied(true);
      toast.success('Código PIX copiado!');
      setTimeout(() => setCopied(false), 3000);
    } catch {
      toast.error('Erro ao copiar');
    }
  };

  const formatarTempo = (segundos) => {
    const m = Math.floor(segundos / 60);
    const s = segundos % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  if (!carga) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800">Carga não encontrada</h2>
          <p className="text-sm text-gray-500 mt-2">Volte e tente novamente</p>
          <Button onClick={() => navigate('/buscar-cargas')} className="mt-4 bg-blue-600">
            Buscar Cargas
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-blue-50 py-8">
      <div className="max-w-lg mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
        </Button>

        <Card className="border-0 shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-700 to-blue-500 p-6 text-white">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold">Pagamento Seguro</h1>
                <p className="text-blue-100 text-sm">Via Mercado Pago PIX</p>
              </div>
            </div>
          </div>

          <CardContent className="p-6 space-y-6">
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Carga</p>
              <p className="font-semibold text-gray-900 mt-1">{carga.titulo || 'Carga BoraFrete'}</p>
              <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
                <span>{carga.origem_cidade} - {carga.origem_estado}</span>
                <span>→</span>
                <span>{carga.destino_cidade} - {carga.destino_estado}</span>
              </div>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-500">Taxa de Agenciamento</p>
              <p className="text-4xl font-bold text-blue-700 mt-1">
                R$ {TAXA_AGENCIAMENTO.toFixed(2).replace('.', ',')}
              </p>
              <p className="text-xs text-gray-400 mt-1">Pagamento único e seguro</p>
            </div>

            {status === 'idle' && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <CreditCard className="w-6 h-6 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-900">Pronto para pagar?</p>
                    <p className="text-sm text-blue-600">Gere o QR Code PIX e escaneie com seu app bancário.</p>
                  </div>
                </div>
                <Button onClick={criarPagamento} className="w-full h-14 text-lg font-bold bg-green-600 hover:bg-green-700 shadow-lg shadow-green-200">
                  <QrCode className="w-5 h-5 mr-2" /> Gerar QR Code PIX
                </Button>
              </div>
            )}

            {status === 'loading' && (
              <div className="flex flex-col items-center py-8">
                <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-3" />
                <p className="font-medium text-gray-700">Gerando QR Code...</p>
              </div>
            )}

            {status === 'pending' && (
              <div className="space-y-4">
                <div className="bg-white rounded-xl border-2 border-dashed border-blue-200 p-6 text-center">
                  {qrCodeBase64 ? (
                    <>
                      <img src={`data:image/png;base64,${qrCodeBase64}`} alt="QR Code PIX" className="w-56 h-56 mx-auto" />
                      <p className="text-sm text-gray-500 mt-3">Escaneie com seu app bancário</p>
                    </>
                  ) : (
                    <div className="w-56 h-56 bg-gray-100 rounded-lg mx-auto flex items-center justify-center">
                      <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <p className="text-xs text-gray-500 mb-2">Ou copie o código PIX:</p>
                  <div className="flex items-center gap-2">
                    <input type="text" value={qrCodeText} readOnly className="flex-1 text-xs bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-600 truncate" />
                    <Button onClick={copiarCodigo} variant="outline" size="sm" className={copied ? 'bg-green-50 border-green-300 text-green-700' : ''}>
                      {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-center gap-2 text-amber-600">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm font-medium">Expira em: {formatarTempo(timeLeft)}</span>
                </div>

                <div className="flex items-center gap-2 justify-center p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <RefreshCw className="w-4 h-4 text-blue-600 animate-spin" />
                  <span className="text-sm text-blue-700">Aguardando pagamento...</span>
                </div>

                {ticketUrl && (
                  <a href={ticketUrl} target="_blank" rel="noopener noreferrer" className="block text-center text-sm text-blue-600 hover:underline">
                    <Smartphone className="w-4 h-4 inline mr-1" /> Abrir no app do Mercado Pago
                  </a>
                )}
              </div>
            )}

            {status === 'approved' && (
              <div className="space-y-4">
                <div className="flex flex-col items-center py-6">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h2 className="text-xl font-bold text-green-700">Pagamento Confirmado!</h2>
                  <p className="text-sm text-gray-600 mt-2">Taxa de agenciamento paga com sucesso.</p>
                </div>

                <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-800">Contrato Liberado!</span>
                  </div>
                  <p className="text-sm text-green-700">Redirecionando para o contrato...</p>
                </div>

                <Button onClick={() => navigate(`/contrato/${carga.id}`)} className="w-full h-12 font-bold bg-blue-600 hover:bg-blue-700">
                  <FileText className="w-5 h-5 mr-2" /> Ver Contrato Agora
                </Button>
              </div>
            )}

            {status === 'rejected' && (
              <div className="space-y-4">
                <div className="flex flex-col items-center py-6">
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
                    <XCircle className="w-10 h-10 text-red-600" />
                  </div>
                  <h2 className="text-xl font-bold text-red-700">Pagamento Não Realizado</h2>
                </div>
                <Button onClick={criarPagamento} className="w-full h-12 font-bold bg-blue-600 hover:bg-blue-700">
                  <RefreshCw className="w-5 h-5 mr-2" /> Tentar Novamente
                </Button>
              </div>
            )}

            {status === 'error' && (
              <div className="space-y-4">
                <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                  <AlertTriangle className="w-5 h-5 text-red-600 inline mr-2" />
                  <span className="font-semibold text-red-800">Erro</span>
                  <p className="text-sm text-red-700 mt-1">{errorMsg}</p>
                </div>
                <Button onClick={criarPagamento} className="w-full h-12 font-bold bg-blue-600 hover:bg-blue-700">
                  <RefreshCw className="w-5 h-5 mr-2" /> Tentar Novamente
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-6 flex items-center justify-center gap-2 text-gray-400">
          <Shield className="w-4 h-4" />
          <span className="text-xs">Pagamento seguro pelo Mercado Pago</span>
        </div>
      </div>
    </div>
  );
}